import { z } from "zod";

import type { AuditChange } from "./organizations";
import {
	describeNewParty,
	describePartyChanges,
	endDateAfterSince,
	endDateNotBeforeSince,
	missingPartyFields,
	PARTY_CONTACT_AUDIT_FIELDS,
	partyAddedAudit,
	partyContactFields,
	partyContactFrom,
	partyContactRecordFields,
	partyDateField,
	partyFieldsFrom,
	partyRecordFields,
	partyRemovedAudit,
	partyRequestFields,
	partyUpdatedAudit,
	type PartyAudit,
	type PartyField,
	type PartyRequirement,
} from "./parties";

// "Other" parties: the counterparties that are neither staff, nor suppliers, nor customers — the bank, the insurer,
// the investor, the landlord. A generic record: a name, a label saying what it is, whom to contact, and dates. What
// it shares with every other kind of party comes from `./parties.ts`; what is here is what makes it this kind. The
// API and the frontend share these.

export const otherPartySchema = z.object({
	...partyRecordFields,
	...partyContactRecordFields,
	/**
	 * What this party is, in the organization's own words: "Bank", "Insurer", "Landlord". Optional here and nowhere
	 * else: a person's request always carries one, and only the Directory agent adds a party without it
	 * (`OTHER_PARTY_REQUIREMENTS`).
	 */
	label: z.string().min(1).max(60).optional(),
});
export type OtherParty = z.infer<typeof otherPartySchema>;

/** The fields of an "other" party that a request carries, trimmed as the form sends them. */
const otherPartyFieldsSchema = z.object({
	...partyRequestFields,
	...partyContactFields,
	label: z.string().trim().min(1, "Say what this is").max(60, "Use at most 60 characters"),
});

/**
 * What the form sends to add or to change an "other" party. Add and change carry the same fields: unlike a person,
 * there is nothing the API owns that a client could send by mistake, and the note is written from the first save.
 */
export const otherPartyRequestSchema = otherPartyFieldsSchema.refine(endDateAfterSince, endDateNotBeforeSince);
export type OtherPartyRequest = z.infer<typeof otherPartyRequestSchema>;

/**
 * The record an add or a change leaves behind: the id and the day it was added are the API's, and everything else is
 * what the request carried — a field somebody left empty left out rather than kept as "", and a note of nothing but
 * spaces no note at all. The two write handlers and the frontend's fake API all build a record this way, so none of
 * the three can drift from the others.
 */
export function otherPartyFrom(id: string, addedAt: string, request: OtherPartyRequest): OtherParty {
	return {
		id,
		...partyFieldsFrom(request),
		...partyContactFrom(request),
		label: request.label,
		addedAt,
	};
}

/**
 * What a person must fill in before an "other" party is complete, in form order and under the form's labels. A
 * person's request always carries both; the Directory agent may add a party with neither, and the Directory shows it
 * as incomplete until somebody fills them in.
 */
export const OTHER_PARTY_REQUIREMENTS: readonly PartyRequirement<OtherParty>[] = [
	["label", "Label"],
	["since", "Start date"],
];

/** The labels of what an "other" party still lacks, in form order: none once somebody has filled it in. */
export function missingOtherPartyFields(party: OtherParty): string[] {
	return missingPartyFields(party, OTHER_PARTY_REQUIREMENTS);
}

/** The line under an "other" party's name: what it is, in the organization's words, or that nobody has said yet. */
export function otherPartyDetail(party: OtherParty): string {
	return party.label ?? "No label yet";
}

/**
 * Every field of an "other" party the audit log names, in the order an entry lists them. The note is not among them:
 * `describeNewParty` and `describePartyChanges` report what became of it and never a word of what it says.
 */
const OTHER_PARTY_FIELDS: readonly PartyField<OtherParty>[] = [
	["Name", party => party.name],
	["Label", party => party.label],
	...PARTY_CONTACT_AUDIT_FIELDS,
	partyDateField("Start date", party => party.since),
	partyDateField("End date", party => party.endDate),
];

/**
 * A new "other" party's values: every field it was added with, and nothing before it. An optional field nobody filled
 * in reads as `UNSPECIFIED_VALUE`, and a date nobody gave as `UNSPECIFIED_DATE`, the way `describeNewPerson` does.
 */
export function describeNewOtherParty(party: OtherParty): AuditChange[] {
	return describeNewParty(party, OTHER_PARTY_FIELDS);
}

/** What changed between two versions of an "other" party, newest value first, for the audit log. */
export function describeOtherPartyChanges(before: OtherParty, after: OtherParty): AuditChange[] {
	return describePartyChanges(before, after, OTHER_PARTY_FIELDS);
}

/** The audit entry for a party added to the Directory: "Added party" / "Mercury · Bank". */
export function otherPartyAddedAudit(party: OtherParty): PartyAudit {
	return partyAddedAudit(party.name, otherPartyDetail(party));
}

/** The audit entry for a party whose record changed: "Updated party" / "Mercury · Label: Bank · Note: Changed". */
export function otherPartyUpdatedAudit(party: OtherParty, changes: readonly AuditChange[]): PartyAudit {
	return partyUpdatedAudit(party.name, changes);
}

/** The audit entry for a party taken out of the Directory: "Removed party" / "Mercury · Bank". */
export function otherPartyRemovedAudit(party: OtherParty): PartyAudit {
	return partyRemovedAudit(party.name, otherPartyDetail(party));
}
